/**
 * 尋找給定日期在股票資料中的索引。
 * 如果日期不存在，則根據 'findNext' 參數決定是尋找下一個最近的交易日還是上一個。
 * @param {Array<Object>} stockData - 已排序的股票資料陣列，每項包含 { date: string, adj_close: number }
 * @param {string} targetDateStr - 目標日期字串 (YYYY-MM-DD)
 * @param {boolean} findNext - true: 若日期不存在，找下一個最近交易日; false: 找上一個最近交易日
 * @returns {number} - 股票資料中的索引，若找不到則返回 -1
 */
export const findDateIndex = (stockData, targetDateStr, findNext = true) => {
  if (!stockData || stockData.length === 0) return -1;
  const targetTime = new Date(targetDateStr).getTime();

  let bestIndex = -1;

  for (let i = 0; i < stockData.length; i++) {
    const currentTime = new Date(stockData[i].date).getTime();
    if (currentTime === targetTime) {
      return i; // 精確匹配
    }
    if (findNext) {
      if (currentTime > targetTime) {
        return i; // 找到第一個晚於目標日期的日期
      }
    } else {
      if (currentTime < targetTime) {
        bestIndex = i; // 記錄目前為止最接近且早於目標的日期
      } else if (currentTime > targetTime && bestIndex !== -1) {
        return bestIndex; // 目標日期介於 bestIndex 和 i 之間，返回 bestIndex
      } else if (currentTime > targetTime && bestIndex === -1) {
        // 如果第一個資料點就晚於目標日期，且是找之前的，則無效
        return -1;
      }
    }
  }
  // 如果循環結束
  // 若是 findNext 且沒找到，表示所有日期都早於 targetDate，無效
  // 若是 !findNext，bestIndex 會是最後一個早於 targetDate 的日期，或者 -1 (如果所有日期都晚於 targetDate)
  return findNext ? -1 : bestIndex;
};

/**
 * 獲取指定日期或下一個最近交易日的股價資料。
 * @param {Array<Object>} stockData - 股票資料
 * @param {string} dateStr - 日期字串
 * @returns {Object|null} - 包含 { date: string, adj_close: number } 的物件，或 null
 */
export const getPriceDataForDate = (stockData, dateStr) => {
  const index = findDateIndex(stockData, dateStr, true);
  return index !== -1 ? stockData[index] : null;
};

/**
 * 檢查日期是否在指定的開始和結束日期之間 (包含邊界)。
 * @param {string} currentDateStr - 目前日期字串
 * @param {string} startDateStr - 開始日期字串
 * @param {string} endDateStr - 結束日期字串
 * @returns {boolean}
 */
export const isDateInRange = (currentDateStr, startDateStr, endDateStr) => {
  const currentTime = new Date(currentDateStr).getTime();
  const startTime = new Date(startDateStr).getTime();
  const endTime = new Date(endDateStr).getTime();
  return currentTime >= startTime && currentTime <= endTime;
};

/**
 * 計算兩個日期之間的天數。
 * @param {string} dateStr1
 * @param {string} dateStr2
 * @returns {number}
 */
export const daysBetween = (dateStr1, dateStr2) => {
  const d1 = new Date(dateStr1);
  const d2 = new Date(dateStr2);
  const diffTime = Math.abs(d2 - d1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};